/**
 * Lightweight markdown renderer for QCM statements.
 * Supports: ```fenced code blocks```, `inline code`, line breaks.
 * Returns a React fragment with safe escaping (no dangerouslySetInnerHTML).
 */
import React from "react";

const codeBlockStyle = {
  fontFamily: "Consolas, Menlo, Monaco, monospace",
  background: "#f1f5f9",
  border: "1px solid #e2e8f0",
  borderRadius: "4px",
  padding: "10px 12px",
  fontSize: "13px",
  whiteSpace: "pre-wrap",
  overflow: "auto",
  margin: "8px 0",
  color: "#0f172a",
};

const inlineCodeStyle = {
  fontFamily: "Consolas, Menlo, Monaco, monospace",
  background: "#f1f5f9",
  padding: "1px 5px",
  borderRadius: "3px",
  fontSize: "0.92em",
  color: "#1e293b",
};

function renderInline(text, keyBase) {
  // splits on `inline code` keeping the matched groups
  const parts = text.split(/(`[^`\n]+`)/g);
  return parts.map((part, i) => {
    if (part.startsWith("`") && part.endsWith("`") && part.length > 2) {
      return (
        <code key={`${keyBase}-c-${i}`} style={inlineCodeStyle}>
          {part.slice(1, -1)}
        </code>
      );
    }
    // Preserve single newlines as <br>
    const subLines = part.split("\n");
    return subLines.map((line, j) => (
      <React.Fragment key={`${keyBase}-l-${i}-${j}`}>
        {line}
        {j < subLines.length - 1 && <br />}
      </React.Fragment>
    ));
  });
}

export default function RichText({ children, className = "" }) {
  const text = children || "";
  if (!text) return null;

  const elements = [];
  const fenceRe = /```[a-zA-Z0-9_+-]*\n?([\s\S]*?)```/g;
  let lastIndex = 0;
  let match;
  let key = 0;

  while ((match = fenceRe.exec(text)) !== null) {
    if (match.index > lastIndex) {
      const before = text.slice(lastIndex, match.index);
      const paragraphs = before.split(/\n\s*\n/);
      for (const p of paragraphs) {
        if (p.trim()) {
          elements.push(
            <p key={`p-${key++}`} className="leading-relaxed whitespace-pre-wrap m-0">
              {renderInline(p, `p${key}`)}
            </p>
          );
        }
      }
    }
    const code = match[1].replace(/\n$/, "");
    elements.push(
      <pre key={`pre-${key++}`} style={codeBlockStyle}>
        <code>{code}</code>
      </pre>
    );
    lastIndex = fenceRe.lastIndex;
  }
  if (lastIndex < text.length) {
    const rest = text.slice(lastIndex);
    const paragraphs = rest.split(/\n\s*\n/);
    for (const p of paragraphs) {
      if (p.trim()) {
        elements.push(
          <p key={`p-${key++}`} className="leading-relaxed whitespace-pre-wrap m-0">
            {renderInline(p, `p${key}`)}
          </p>
        );
      }
    }
  }

  return <div className={`space-y-2 ${className}`}>{elements}</div>;
}
